import { LANDING_VARIANT } from "@/config/landing";

/**
 * Default brand glyph, switched by deployment mode:
 *
 *   - "demo" (template marketing mode) — Chevron Stack (kept for
 *     that landing variant only; authenticated shell shows Reclaim chrome).
 *   - "custom" (default / Reclaim platform) — deep-teal Reclaim "R" mark
 *     aligned with Agency Atelier. Agencies replace it via Branding (logoUrl).
 *
 * Use `<LogoMark size={20} />` next to the wordmark, or alone as a glyph.
 */

interface LogoMarkProps {
  /** Square pixel size. Defaults to 20 (matches sidebar usage). */
  size?: number;
  className?: string;
  /** Optional unique suffix when multiple instances render in one document — keeps the gradient defs from colliding across SSR + hydration. */
  idSuffix?: string;
}

export function LogoMark(props: LogoMarkProps) {
  if (LANDING_VARIANT === "custom") return <ReclaimMark {...props} />;
  return <ChevronStackMark {...props} />;
}

/** Reclaim platform mark — deep teal on near-black, Atelier accent family. */
function ReclaimMark({ size = 20, className, idSuffix = "" }: LogoMarkProps) {
  const gradId = `reclaim-grad${idSuffix}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0f766e" />
          <stop offset="55%" stopColor="#0d9488" />
          <stop offset="100%" stopColor="#2dd4bf" />
        </linearGradient>
      </defs>
      <rect width="64" height="64" rx="14" fill="#14181f" />
      <text
        x="32"
        y="42"
        textAnchor="middle"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
        fontWeight="500"
        fontSize="36"
        fill={`url(#${gradId})`}
      >
        R
      </text>
    </svg>
  );
}

/**
 * Platform chevron stack — top points left, middle right, bottom left;
 * chevron tips trace an S while alternating alignment reads as stacked
 * cards.
 */
function ChevronStackMark({ size = 20, className, idSuffix = "" }: LogoMarkProps) {
  const id1 = `cs-mark-1${idSuffix}`;
  const id2 = `cs-mark-2${idSuffix}`;
  const id3 = `cs-mark-3${idSuffix}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={id1} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#6366f1" />
          <stop offset="100%" stopColor="#7c3aed" />
        </linearGradient>
        <linearGradient id={id2} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#8b5cf6" />
          <stop offset="100%" stopColor="#a855f7" />
        </linearGradient>
        <linearGradient id={id3} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#c026d3" />
          <stop offset="100%" stopColor="#ec4899" />
        </linearGradient>
      </defs>

      {/* Top chevron — points LEFT, right-aligned */}
      <path d="M 56 8 L 18 8 L 8 16 L 18 24 L 56 24 Z" fill={`url(#${id1})`} />

      {/* Middle chevron — points RIGHT, left-aligned */}
      <path d="M 8 28 L 46 28 L 56 36 L 46 44 L 8 44 Z" fill={`url(#${id2})`} />

      {/* Bottom chevron — points LEFT, right-aligned */}
      <path d="M 56 48 L 18 48 L 8 56 L 18 60 L 56 60 Z" fill={`url(#${id3})`} />
    </svg>
  );
}
